import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaPalette,
  FaSave,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import {
  emptyGroupForm,
  getGroupColorOption,
  GROUP_COLORS,
  mapGroupToForm,
} from "../utils/groupesUtils";

export default function GroupFormDrawer({
  isOpen,
  mode = "create",
  group,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyGroupForm);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && group) {
      setFormData(mapGroupToForm(group));
    } else {
      setFormData(emptyGroupForm);
    }
  }, [isOpen, isEdit, group]);

  const title = isEdit ? "Modifier un groupe" : "Créer un groupe";

  const subtitle = isEdit
    ? "Mettez à jour les horaires, le tarif et la couleur du groupe."
    : "Ajoutez un groupe rattaché à l’association actuellement sélectionnée.";

  const selectedColor = getGroupColorOption(formData.color_groupe);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.nom.trim()) {
      toast.error("Le nom du groupe est obligatoire.");
      return;
    }

    if (isEdit) {
      await onUpdate?.(group.id, formData);
    } else {
      await onCreate?.(formData);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaUsers />}
            title="Informations principales"
            subtitle="Nom du groupe, couleur et visibilité."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Nom du groupe"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                placeholder="Ex : Baby judo, Adultes loisirs..."
              />

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Couleur du groupe
                </label>

                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <select
                    name="color_groupe"
                    value={formData.color_groupe}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
                  >
                    {GROUP_COLORS.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.label}
                      </option>
                    ))}
                  </select>

                  <span
                    className={[
                      "flex h-10 w-10 rounded-2xl ring-1 ring-slate-200 dark:ring-white/10",
                      selectedColor.value,
                    ].join(" ")}
                    title={selectedColor.label}
                  />
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10 sm:col-span-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(formData.is_active)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />

                <span className="inline-flex items-center gap-2">
                  <FaCheckCircle
                    className={
                      formData.is_active ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  Groupe actif et visible
                </span>
              </label>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaClock />}
            title="Horaires"
            subtitle='Format conseillé : "Jour 17h00-18h00", par exemple "Mardi 17h00-18h00".'
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Horaire 1"
                name="horaire1"
                value={formData.horaire1}
                onChange={handleChange}
                placeholder="Mardi 17h00-18h00"
              />

              <InputField
                label="Horaire 2"
                name="horaire2"
                value={formData.horaire2}
                onChange={handleChange}
                placeholder="Samedi 10h00-11h00"
              />
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaEuroSign />}
            title="Public & tarif"
            subtitle="Années de naissance concernées et tarif annuel."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Années de naissance"
                name="annees_naissance"
                value={formData.annees_naissance}
                onChange={handleChange}
                placeholder="Ex : 2016 à 2018"
              />

              <InputField
                label="Tarif annuel"
                name="tarif_groupe"
                type="number"
                value={formData.tarif_groupe}
                onChange={handleChange}
                placeholder="180"
              />
            </div>
          </FormBlock>

          <div className="rounded-[24px] border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
            <FaPalette className="mr-2 inline" />
            La couleur choisie est utilisée dans les tableaux d’administration et
            peut également servir à différencier les créneaux sur le planning
            public de l’association.
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">
            {title}
          </h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}